import { useRef } from 'react';
import styled from 'styled-components';
import { colors, s8, s16 } from '@ardoq/design-tokens';
import { header4 } from '@ardoq/typography';
import { TextOverflow } from '@ardoq/popovers';
import { useResizeObserver } from '@ardoq/hooks';
import { NumberWidgetProps } from './types';
import { EmptyTrend, TrendUI } from './TrendUI';
import { TextFit } from '../atoms/TextFit';
import { type WithI18nLocale } from '@ardoq/i18n';
import {
  getResultLabelLineCount,
  RESULT_LABEL_GAP,
} from './getResultLabelLineCount';

const NumberWidgetContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${RESULT_LABEL_GAP}px;
  padding: ${s16};
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const NumberContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${s8};
  width: 100%;
  flex-shrink: 0;
`;

// using negative margin as an alternative to "line-height: 1"
// so we can use the same number/label gap for different font sizes
const WidgetNumber = styled.div<{
  $limitColor: string;
  $hasTrend: boolean;
}>`
  width: 100%;
  justify-content: center;
  font-weight: 600;
  line-height: 1.4;
  margin: ${({ $hasTrend }) => ($hasTrend ? '0 0 -0.2em 0' : '-0.2em 0')};
  color: ${({ $limitColor }) => $limitColor};
  white-space: nowrap;
`;

const ResultsLabelContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 0;
  overflow: hidden;
`;

const ResultsLabel = styled(TextOverflow)`
  ${header4};
`;

const NumberWidget = ({
  formattedNumber,
  trendProps,
  resultLabel,
  color = colors.textDefault,
  i18nLocale,
}: NumberWidgetProps & WithI18nLocale) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const numberRef = useRef<HTMLDivElement>(null);
  const { height: contentHeight } = useResizeObserver(contentRef);
  const { height: numberHeight } = useResizeObserver(numberRef);

  return (
    <NumberWidgetContainer ref={contentRef}>
      <NumberContainer ref={numberRef}>
        {trendProps &&
          (trendProps.isEmptyTrend ? (
            <EmptyTrend {...trendProps} i18nLocale={i18nLocale} />
          ) : (
            <TrendUI {...trendProps} i18nLocale={i18nLocale} />
          ))}
        <WidgetNumber $limitColor={color} $hasTrend={Boolean(trendProps)}>
          <TextFit value={formattedNumber} />
        </WidgetNumber>
      </NumberContainer>
      {resultLabel && (
        <ResultsLabelContainer>
          <ResultsLabel
            lineClamp={getResultLabelLineCount({ contentHeight, numberHeight })}
          >
            {resultLabel}
          </ResultsLabel>
        </ResultsLabelContainer>
      )}
    </NumberWidgetContainer>
  );
};

export { NumberWidget };
